import * as Sentry from "@sentry/react";

import { ProductNames } from "constants/forecasts.constants";

import { ArpSegment } from "models/UserArpsModel";

import { calculateRestrictedEURMultipleSegments } from "./calculateRestrictedEURMultipleSegments";
import { calculateRestrictedEURSingleSegment } from "./calculateRestrictedEURSingleSegment";

export interface RestrictedEURResult {
  isEndDateLater: boolean;
  restrictedEUR: number;
  qfAtCutoff: number;
  productName?: string; // Product name for toast notifications
}

/**
 * Calculate Restricted/Cutoff EUR for a product
 *
 * This calculates what the EUR would be if the product
 * was cut off at the primary product's end date.
 *
 * **IMPORTANT**: This calculation matches WASM's `restrict_secondary_products_end_dates` approach:
 * - WASM generates monthly forecast points (typically on day 1 of each month)
 * - WASM filters monthly points where point.day <= primary.day
 * - Monthly points represent cumulative production through the END of that month
 * - For a primary end date of Nov 22, WASM keeps points up through Nov 1
 * - The Nov 1 point's cumulative represents production through Nov 30
 *
 * Therefore, we calculate:
 * 1. EUR to END of month containing primary end date (e.g., Nov 30 for Nov 22 primary)
 * 2. qfAtCutoff as rate at start of that month (e.g., Nov 1 for Nov 22 primary)
 *
 * **Single vs Multiple Segments**:
 * - Both use the same approach (end of current month containing primary date)
 * - Separated into different files for clarity and maintainability
 *
 * @param productSegments - Array of segments for the product (must not be empty for calculation)
 * @param isPrimaryProduct - Whether this is the primary product (if true, no calculation needed)
 * @param primaryProductEndDate - End date of the primary product (cutoff point)
 * @returns Object containing:
 *   - isEndDateLater: true if product extends beyond primary product
 *   - restrictedEUR: Cumulative volume to end of current month (in MCF)
 *   - qfAtCutoff: Flow rate at start of cutoff month
 *
 */
export function calculateRestrictedEUR(
  productSegments: ArpSegment[],
  isPrimaryProduct: boolean | undefined,
  primaryProductEndDate: Date | null | undefined
): RestrictedEURResult {
  const defaultResult: RestrictedEURResult = {
    isEndDateLater: false,
    restrictedEUR: 0,
    qfAtCutoff: 0
  };

  if (!primaryProductEndDate || productSegments.length === 0) {
    return defaultResult;
  }

  if (isPrimaryProduct === true) {
    return defaultResult;
  }

  try {
    // Use original segment timestamps - no rounding needed
    const lastSegment = productSegments[productSegments.length - 1];
    const currentProductEndDate = new Date(lastSegment.endDate);

    const isLater = currentProductEndDate > primaryProductEndDate;
    if (!isLater) {
      return defaultResult;
    }

    // Use separate calculation functions based on segment count
    let result;
    if (productSegments.length === 1) {
      result = calculateRestrictedEURSingleSegment(
        productSegments[0],
        primaryProductEndDate
      );
    } else {
      result = calculateRestrictedEURMultipleSegments(
        productSegments,
        primaryProductEndDate
      );
    }

    return {
      isEndDateLater: true,
      restrictedEUR: result.restrictedEUR,
      qfAtCutoff: result.qfAtCutoff,
      productName: productSegments[0]?.product // Include product name for toast notification
    };
  } catch (error) {
    Sentry.withScope((scope) => {
      let adjustedCutoffDate: string | undefined;
      let cutoffMonthStartDate: string | undefined;
      if (primaryProductEndDate && productSegments.length > 0) {
        // Determine if this is an oil product
        const isOilProduct =
          productSegments[0]?.product?.toUpperCase() === ProductNames.Oil;
        const monthOffset = isOilProduct ? -1 : 0;

        // Calculate to end of current month (or previous month for oil)
        const cutoffDate = new Date(
          Date.UTC(
            primaryProductEndDate.getUTCFullYear(),
            primaryProductEndDate.getUTCMonth() + 1 + monthOffset,
            0, // Day 0 = last day of target month
            23,
            59,
            59,
            999
          )
        );
        adjustedCutoffDate = cutoffDate.toISOString();

        const monthStart = new Date(
          Date.UTC(
            primaryProductEndDate.getUTCFullYear(),
            primaryProductEndDate.getUTCMonth() + monthOffset,
            1,
            0,
            0,
            0,
            0
          )
        );
        cutoffMonthStartDate = monthStart.toISOString();
      }

      scope.setContext("calculation", {
        productName: productSegments[0]?.product,
        segmentCount: productSegments.length,
        primaryProductEndDate: primaryProductEndDate?.toISOString(),
        adjustedCutoffDate,
        cutoffMonthStartDate,
        isPrimaryProduct,
        firstSegmentStartDate: productSegments[0]?.startDate,
        lastSegmentEndDate: productSegments[productSegments.length - 1]?.endDate
      });
      scope.setContext("declineParameters", {
        qi: productSegments[0]?.qi,
        di: productSegments[0]?.di,
        b: productSegments[0]?.b,
        qf: productSegments[0]?.qf
      });
      scope.setLevel("error");
      scope.setTag("function", "calculateRestrictedEUR");

      Sentry.captureException(error);
    });

    return defaultResult;
  }
}
